import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, UserPlus, Mail, Lock, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import CurrencyIcon from '../components/CurrencyIcon';
import { Floating } from '../components/Motion';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const Auth = () => {
  const { t } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, phone);
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-black flex" data-testid="auth-container">
      {/* Left dark panel — Revolut style */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 relative overflow-hidden">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Logo size="default" dark />
        </motion.div>
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="text-white font-extrabold text-5xl xl:text-6xl leading-[1.05] tracking-[-0.03em] mb-6" style={{ fontFamily: 'Manrope' }}>
            {t.hero?.title || "Bien plus qu'un simple échange"} {t.hero?.titleHighlight || ''}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="text-white/50 text-lg max-w-md">
            {t.hero?.description || 'Crypto, devises et transferts internationaux. Rapide, sécurisé, transparent.'}
          </motion.p>
        </div>
        <Floating className="self-start">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}
            className="glass-card-dark p-6 flex items-center space-x-4">
            {['BTC', 'ETH', 'USDT', 'SOL'].map(s => (
              <div key={s} className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
                <CurrencyIcon symbol={s} size={26} />
              </div>
            ))}
            <div className="text-white/70 text-sm font-semibold">+ 16 cryptos</div>
          </motion.div>
        </Floating>
      </div>

      {/* Right form panel */}
      <div className="flex-1 bg-white lg:rounded-l-[40px] flex flex-col">
        <div className="flex items-center justify-between px-8 pt-6">
          <div className="lg:hidden"><Logo size="default" /></div>
          <div className="ml-auto"><LanguageSelector /></div>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="w-full max-w-md">
            <h2 className="font-extrabold text-4xl mb-2 text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="auth-title">
              {isLogin ? (t.auth?.login || 'Connexion') : (t.auth?.register || 'Inscription')}
            </h2>
            <p className="text-gray-400 mb-8">{t.common?.authSubtitle || 'Accédez à votre compte alphaswapp'}</p>

            <button
              onClick={handleGoogleLogin}
              className="w-full bg-gray-50 border border-gray-200 text-gray-900 py-3.5 rounded-full hover:bg-gray-100 transition-colors font-semibold flex items-center justify-center space-x-3 mb-6"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>{t.common?.continueGoogle || 'Continuer avec Google'}</span>
            </button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
              <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-400">{t.auth?.or || 'ou'}</span></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-600" htmlFor="email">{t.auth?.email || 'Email'}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 w-5 h-5" />
                  <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-2xl pl-12 pr-4 py-3.5 text-gray-900 outline-none transition-colors placeholder-gray-300"
                    placeholder="votre@email.com" required data-testid="email-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-600" htmlFor="password">{t.auth?.password || 'Mot de passe'}</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 w-5 h-5" />
                  <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-2xl pl-12 pr-4 py-3.5 text-gray-900 outline-none transition-colors placeholder-gray-300"
                    placeholder="••••••••" required data-testid="password-input" />
                </div>
              </div>

              {!isLogin && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-600" htmlFor="phone">{t.auth?.phone || 'Téléphone (optionnel)'}</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 w-5 h-5" />
                    <input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 rounded-2xl pl-12 pr-4 py-3.5 text-gray-900 outline-none transition-colors placeholder-gray-300"
                      placeholder="+33 6 12 34 56 78" data-testid="phone-input" />
                  </div>
                </div>
              )}

              <button type="submit" disabled={loading}
                className="w-full btn-pill-dark py-4 font-semibold flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="auth-submit-btn">
                {loading ? (
                  <span>{t.common?.loading || 'Chargement...'}</span>
                ) : (
                  <>
                    {isLogin ? <LogIn className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                    <span>{isLogin ? (t.auth?.loginBtn || 'Se connecter') : (t.auth?.registerBtn || "S'inscrire")}</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button onClick={() => setIsLogin(!isLogin)}
                className="text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium"
                data-testid="auth-toggle-btn">
                {isLogin ? (t.auth?.noAccount || "Pas encore de compte ? S'inscrire") : (t.auth?.hasAccount || 'Déjà un compte ? Se connecter')}
              </button>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100 text-center text-sm text-gray-400">
              <p>{t.auth?.terms || 'En vous connectant, vous acceptez nos'}</p>
              <p>
                <Link to="/agreement" className="text-gray-900 font-semibold hover:underline">{t.auth?.termsLink || "Conditions d'utilisation"}</Link>
                {' '}·{' '}
                <Link to="/privacy" className="text-gray-900 font-semibold hover:underline">{t.auth?.privacyLink || 'Politique de confidentialité'}</Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
